import React, { useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import ProofSheet from './ProofSheet';
import { toPng } from 'html-to-image';
import download from 'downloadjs';
import { toast } from 'sonner';
import { Download, Printer } from 'lucide-react';

const ProofSheetDialog = ({ open, onOpenChange, text, language }) => {
  const proofSheetRef = useRef(null);
  const [proofTitle, setProofTitle] = React.useState('Monument Inscription Proof');

  const handlePrint = () => {
    if (proofSheetRef.current) {
      const printWindow = window.open('', '', 'width=800,height=600');
      const doc = printWindow.document;
      
      // Create HTML structure using DOM methods instead of document.write
      const html = doc.createElement('html');
      const head = doc.createElement('head');
      const body = doc.createElement('body');
      
      // Create title
      const title = doc.createElement('title');
      title.textContent = 'Monument Proof Sheet';
      head.appendChild(title);
      
      // Create font link
      const fontLink = doc.createElement('link');
      fontLink.href = 'https://fonts.googleapis.com/css2?family=Frank+Ruhl+Libre:wght@400;500;700&display=swap';
      fontLink.rel = 'stylesheet';
      head.appendChild(fontLink);
      
      // Create style element
      const style = doc.createElement('style');
      style.textContent = `
        @page { size: letter; margin: 0.5in; }
        body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
        .font-hebrew { font-family: "Frank Ruhl Libre", serif; }
        .font-arabic { font-family: "Noto Naskh Arabic", Amiri, serif; }
      `;
      head.appendChild(style);
      
      // Clone and append proof sheet content
      const content = proofSheetRef.current.cloneNode(true);
      body.appendChild(content);
      
      // Assemble document
      html.appendChild(head);
      html.appendChild(body);
      doc.appendChild(html);
      
      doc.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
      toast.success('Print dialog opened');
    }
  };

  const handleExportPDF = async () => {
    if (!proofSheetRef.current) {
      toast.error('Proof sheet not found');
      return;
    }

    try {
      toast.info('Generating proof sheet image...');

      // Wait for all images inside the proof sheet to finish loading
      const images = Array.from(proofSheetRef.current.querySelectorAll('img'));
      await Promise.all(
        images.map((img) =>
          img.complete && img.naturalWidth > 0
            ? Promise.resolve()
            : new Promise((resolve) => {
                img.addEventListener('load', resolve, { once: true });
                img.addEventListener('error', resolve, { once: true });
              })
        )
      );

      // Use html2canvas for better compatibility
      const html2canvas = (await import('html2canvas')).default;
      
      const canvas = await html2canvas(proofSheetRef.current, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
      });
      
      canvas.toBlob((blob) => {
        if (blob) {
          download(blob, `monument-proof-sheet-${Date.now()}.png`);
          toast.success('Proof sheet exported successfully!');
        } else {
          toast.error('Failed to generate proof sheet');
        }
      });
      
    } catch (error) {
      console.error('Error exporting proof sheet:', error);
      toast.error('Export failed. Please use browser print function as alternative.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] overflow-y-auto" data-testid="proof-sheet-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral text-2xl">Funeral Home Proof Sheet</DialogTitle>
          <DialogDescription>
            This format matches the standard funeral home submission requirements with individual letter boxes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Label htmlFor="proof-title">Proof Sheet Title</Label>
              <Input
                id="proof-title"
                value={proofTitle}
                onChange={(e) => setProofTitle(e.target.value)}
                placeholder="Monument Inscription Proof"
                className="mt-1"
                data-testid="proof-title-input"
              />
            </div>
            <div className="flex gap-2 pt-6">
              <Button
                onClick={handlePrint}
                variant="outline"
                className="transition-all hover:scale-95"
                data-testid="print-proof-button"
              >
                <Printer className="mr-2 h-4 w-4" />
                Print
              </Button>
              <Button
                onClick={handleExportPDF}
                variant="default"
                className="bg-gold-500 hover:bg-gold-600 text-white transition-all hover:scale-95"
                data-testid="export-proof-button"
              >
                <Download className="mr-2 h-4 w-4" />
                Export PNG
              </Button>
            </div>
          </div>

          {/* Proof Sheet Preview */}
          <div className="border-2 border-stone-300 rounded-lg overflow-auto max-h-[60vh] bg-stone-100 p-4">
            <div ref={proofSheetRef} className="bg-white shadow-lg mx-auto" style={{ width: '8.5in' }}>
              <ProofSheet text={text} title={proofTitle} language={language} />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProofSheetDialog;